"use client";

import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle, AlertCircle, ExternalLink } from "lucide-react";
import Image from "next/image";

interface IBKRSetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function IBKRSetupModal({ open, onOpenChange, onSuccess }: IBKRSetupModalProps) {
  const [queryId, setQueryId] = useState("");
  const [queryToken, setQueryToken] = useState("");
  const [accountName, setAccountName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch("/api/broker/ibkr/connect", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          queryId,
          queryToken,
          accountName: accountName || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || "Failed to connect");
      }

      setSuccess(true);
      
      // Reset form after 2 seconds and close
      setTimeout(() => {
        setQueryId("");
        setQueryToken("");
        setAccountName("");
        setSuccess(false);
        onOpenChange(false);
        onSuccess?.();
      }, 2000);

    } catch (err: any) {
      setError(err.message || "Failed to connect to IBKR");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setQueryId("");
      setQueryToken("");
      setAccountName("");
      setError(null);
      setSuccess(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect Interactive Brokers (Direct)</DialogTitle>
          <DialogDescription>
            Set up IBKR Flex Query API to sync your trading history directly
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Setup Instructions */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm">Setup Instructions:</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>
                Log in to your IBKR account and navigate to:{" "}
                <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">
                  Performance & Reports → Flex Queries → ⚙ Activity Flex Query 
                </span>
                <br></br>
                <br></br>
                Select the following options: <br></br>
                <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">
                - Trades (Executions, Orders) <br></br>
                - Option Exercises, Assignments and Expirations <br></br>
                - Cash Reports (Dividends) <br></br>
                - Cash Transactions (Deposits and Withdrawals, Dividends) <br></br>
                - Transfers <br></br>
                - Open Positions <br></br>
                </span>
                
              </li>
              <li>Under <strong>Delivery Configuration</strong> select <strong>Last 365 Days</strong> as the period</li>
              <li>Keep the default data formatting (see photo): yyyyMMdd, HHmmss, ; (semi-colon), XML format</li>
              <li>Click <strong>Continue and take a note of the Query ID</strong></li>
              <li>If you already don&apos;t have a token, <strong>Generate a new token</strong> under <strong>Flex Web Service Configuration</strong> and select <strong>Expire After: 1y</strong></li>
              <li>Copy the <strong>Query ID</strong> and <strong>Token</strong></li>
              <li>Paste them in the form below</li>
            </ol>

            {/* Reference Image */}
            <div className="border rounded-lg overflow-hidden">
              <Image
                src="/_static/landing/ibkr-flex-settings.jpeg"
                alt="IBKR Flex Query Settings"
                width={600}
                height={400}
                className="w-full h-auto"
                unoptimized
              />
            </div>

            <Alert>
              <AlertDescription className="text-xs">
                <strong>Note:</strong> The Flex Query should include trade data. If you haven&apos;t 
                set up a Flex Query yet, create one that includes Trades and Cash Report sections.
              </AlertDescription>
            </Alert>
          </div>

          {/* Connection Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="queryId">Query ID *</Label>
              <Input
                id="queryId"
                type="text"
                placeholder="Enter your Flex Query ID"
                value={queryId}
                onChange={(e) => setQueryId(e.target.value)}
                required
                disabled={loading || success}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="queryToken">Token *</Label>
              <Input
                id="queryToken"
                type="password"
                placeholder="Enter your Flex Query Token"
                value={queryToken}
                onChange={(e) => setQueryToken(e.target.value)}
                required
                disabled={loading || success}
              />
              <p className="text-xs text-muted-foreground">
                Your token is encrypted and stored securely
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountName">Account Name (Optional)</Label>
              <Input
                id="accountName"
                type="text"
                placeholder="e.g., Main Trading Account"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                disabled={loading || success}
              />
              <p className="text-xs text-muted-foreground">
                Give this connection a friendly name for easy identification
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Success Display */}
            {success && (
              <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-600 dark:text-green-400">
                  Successfully connected! Redirecting...
                </AlertDescription>
              </Alert>
            )}

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={loading || success || !queryId || !queryToken}
                className="flex-1"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {success && <CheckCircle className="mr-2 h-4 w-4" />}
                {loading ? "Testing Connection..." : success ? "Connected!" : "Test & Connect"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </form>

          {/* Help Links */}
          <div className="pt-4 border-t">
            <p className="text-xs text-muted-foreground mb-2">Need help?</p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.interactivebrokers.com/en/software/am/am/reports/flex_queries.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
              >
                IBKR Flex Query Guide
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

